import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AddEditTimeslotModal } from './AddEditTimeslotModal';
import {
  GetTimeslotsQuery,
  GetTimeslotsQueryVariables,
  StartOfWeekFormatSetting,
  TimeFormatSetting
} from 'models/graphql';
import { GetTimeslots_workspace_timeslots } from 'models/legacy-graphql';
import classes from './PostingPlan.module.scss';
import classNames from 'classnames';
import { BulkDeleteTimeslotsModal } from './BulkDeleteTimeslotsModal';
import { TopNavContext } from '../../../providers/TopNavProvider';
import { useDimensions } from '../../../hooks/useDimensions';
import { TimeslotFilterMenu } from './TimeslotFilterMenu';
import { TimeslotFilterMenuViewProps } from './TimeslotFilterMenu/TimeslotFilterMenu.view';
import { TimeslotActionsType, TimeslotSocialProfileType } from './Timeslot/Timeslot.view';
import { topNavOffsetClassesGetter } from '../../../helpers/topNavOffsetClasses';
import { PostingPlanCalendar } from './PostingPlanCalendar';
import { SettingsType, useSettings } from '../../../hooks/useSettings';
import { DeleteTimeslotModal } from './DeleteTimeslotModal';
import { Loader } from '../../../components/containers/Loader';
import { Modal } from '../../../components/containers/Modal';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import {
  CalendarDataType,
  PostingPlanCellDataType,
  TimeslotFilterAction,
  TimeslotFilterType
} from '../../../models/calendar';
import { CategoryType } from '../../../components/filters/CategoryFilterGroup/CategoryFilterGroup.controller';
import { ProfileType } from '../../../components/filters/SocialProfileFilterGroup/SocialProfileFilterGroup.controller';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { BreakpointsEnum } from '../../../constants/breakpoints';
import { TimeslotControllerProps } from './Timeslot/Timeslot.controller';
import { PostingPlanHelpers } from './postingPlanHelpers';
import { PostingPlanProvider } from '../../../providers/PostingPlanProvider';
import { RefetchQueryFunction, Void } from '../../../models/basic-objects';
import { FluidContentLayoutView } from '../../../components/containers/FluidContentLayout/FluidContentLayout.view';
import { PostingPlanCloneCalendar } from './PostingPlanCloneCalendar';
import { BiggerScreenPlaceholder } from '../../../components/containers/BiggerScreenPlaceHolder';
import { Link } from 'react-router-dom';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../Onboarding/constants/content';

export type PostingPlanViewProps = {
  selectedTimeslot: GetTimeslots_workspace_timeslots | undefined;
  filteredTimeslots: GetTimeslots_workspace_timeslots[];
  profiles: ProfileType[];
  categories: CategoryType[];
  timeslotFilterMenuProps: TimeslotFilterMenuViewProps;
  loading: boolean;
  openCreateEditTimeslotModal: boolean;
  setOpenCreateEditTimeslotModal: (openTimeslotModal: boolean) => void;
  openBulkDeleteTimeslotsModal: boolean;
  setOpenBulkDeleteTimeslotsModal: (openBulkDeleteAllModal: boolean) => void;
  openDeleteTimeslotWarningModal: boolean;
  setOpenDeleteTimeslotWarningModal: (openDeleteTimeslotWarningModal: boolean) => void;
  filters: TimeslotFilterType;
  dispatchFilters: Dispatch<TimeslotFilterAction>;
  numberOfWorkspaces: number;
  dateClicked: [number, number] | undefined;
  loadingCalendar: boolean;
  setLoadingCalendar: Dispatch<SetStateAction<boolean>>;
  openFilterModal: boolean;
  setOpenFilterModal: Dispatch<SetStateAction<boolean>>;
  addTimeslotHandler: Void<[number, number]>;
  editTimeslotHandler: (timeslot: GetTimeslots_workspace_timeslots) => void;
  cloneTimeslotHandler: (timeslot: GetTimeslots_workspace_timeslots) => void;
  deleteTimeslotHandler: (timeslot: GetTimeslots_workspace_timeslots, lastItemApproval?: boolean) => void;
  applyMobileFilters: VoidFunction;
  resetMobileFilters: VoidFunction;
  refetchTimeslots: RefetchQueryFunction<GetTimeslotsQuery, GetTimeslotsQueryVariables> | undefined;
  timeslotToClone: GetTimeslots_workspace_timeslots | null;
  setTimeslotToClone: Dispatch<SetStateAction<GetTimeslots_workspace_timeslots | null>>;
};

export type TimeslotMappedData = Omit<TimeslotControllerProps, 'isListView' | 'clone'>;

export const PostingPlanView = (props: PostingPlanViewProps): JSX.Element => {
  const letColRef = useRef<HTMLDivElement>(null);
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.timeslots;

  const { hasScrollbar: hasLeftScrollbar } = useDimensions(letColRef);
  const topNavOffsetContext = useContext(TopNavContext);
  const settings: SettingsType = useSettings();
  const breakpoint = useBreakpoints();

  const dayStartOfWeek =
    settings.selectedOrgSettings?.startOfWeekFormat === StartOfWeekFormatSetting.SUNDAY
      ? StartOfWeekFormatSetting.SUNDAY
      : StartOfWeekFormatSetting.MONDAY;

  const mapTimeslotToEventData = useCallback(
    (timeslot: GetTimeslots_workspace_timeslots): TimeslotMappedData => {
      const acts: TimeslotActionsType = {
        edit: () => props.editTimeslotHandler(timeslot),
        delete: () => props.deleteTimeslotHandler(timeslot),
        clone: () => props.cloneTimeslotHandler(timeslot)
      };

      const tsp: TimeslotSocialProfileType[] = timeslot.socialProfiles
        .map<TimeslotSocialProfileType | null>(tsProfile => {
          const profile = props.profiles.find(p => p.id === tsProfile.id);

          if (profile) {
            const disabled = props.filters.profiles ? profile.id !== props.filters.profiles : false;
            return {
              disabled,
              connected: profile.connected,
              provider: profile.provider,
              network: profile.network,
              genericProfile: profile.genericProfile,
              image: profile.photoUrl,
              id: profile.id,
              name: profile.displayName,
              __typename: tsProfile.__typename
            };
          }
          return null;
        })
        .filter(item => item !== null) as TimeslotSocialProfileType[];

      return {
        actions: acts,
        categoryId: timeslot.category.id,
        colour: timeslot.category.color,
        day: timeslot.day,
        hovering: false, // while we drag other timeslot from the same category
        id: timeslot.id,
        isDragging: false,
        name: timeslot.category.name,
        socialProfiles: tsp,
        time: timeslot.time,
        timeFormat: settings.selectedOrgSettings?.timeFormat || TimeFormatSetting.HOUR_24,
        onboardingContent: timeslot.onboardingContent && showOnboarding && disableDelete
      };
    },
    [props, settings.selectedOrgSettings?.timeFormat, showOnboarding, disableDelete]
  );

  const filteredEventData: CalendarDataType<PostingPlanCellDataType> = useMemo(() => {
    return PostingPlanHelpers.transformEventsData(props.filteredTimeslots.map(mapTimeslotToEventData), dayStartOfWeek);
  }, [props.filteredTimeslots, mapTimeslotToEventData, dayStartOfWeek]);

  const topNavClasses = topNavOffsetClassesGetter(topNavOffsetContext.state.visibility, props.numberOfWorkspaces);
  const leftColumnWrapperClass = classNames(
    classes.LeftSideWrapper,
    'cell',
    'show-for-xxlarge',
    topNavClasses.vheight,
    topNavClasses.top
  );

  const rightColumnWrapperClass = classNames(topNavClasses.vheight, classes.RightSideColumn);
  const leftColumnClass = classNames(classes.LeftSideColumn, hasLeftScrollbar ? classes.LeftSideColumnScroller : null);

  const [shouldRenderCalendar, setShouldRenderCalendar] = useState(false);

  // This timeout workaround is to let the page render the loading screen before the calendar as the calendar
  // on initial load takes quite some time and can block the entire painting thread in the browser.
  useEffect(() => {
    if (!props.loading && !shouldRenderCalendar) {
      setTimeout(() => {
        setShouldRenderCalendar(true);
      }, 1);
    }
  }, [props.loading, shouldRenderCalendar]);

  return (
    <FluidContentLayoutView loading={props.loading}>
      <PostingPlanProvider
        refetchTimeslots={props.refetchTimeslots}
        cellClickHandler={props.addTimeslotHandler}
        timeslotData={filteredEventData}>
        {props.timeslotToClone ? (
          <>
            {breakpoint === BreakpointsEnum.small ? (
              <BiggerScreenPlaceholder />
            ) : (
              <>
                {props.loadingCalendar && <Loader variation={'dark'} size={'large'} type={'overlay'} />}
                <PostingPlanCloneCalendar
                  timeFormat={settings.selectedOrgSettings?.timeFormat || TimeFormatSetting.HOUR_24}
                  exitHandler={() => props.setTimeslotToClone(null)}
                  timeslot={props.timeslotToClone}
                />
              </>
            )}
          </>
        ) : (
          <div className={classes.PostingPlanWrapper}>
            <div className={leftColumnWrapperClass}>
              <div className={leftColumnClass} ref={letColRef}>
                <TimeslotFilterMenu
                  filters={props.filters}
                  categories={props.categories}
                  profiles={props.profiles}
                  dispatchFilters={props.dispatchFilters}
                  formInstance={props.timeslotFilterMenuProps.formInstance}
                  timeslots={props.timeslotFilterMenuProps.timeslots}
                  profilesFilteredTimeslots={props.timeslotFilterMenuProps.profilesFilteredTimeslots}
                  categoriesFilteredTimeslots={props.timeslotFilterMenuProps.categoriesFilteredTimeslots}
                  loading={props.loadingCalendar}
                />
              </div>
            </div>
            <div className={rightColumnWrapperClass}>
              {props.loadingCalendar && <Loader variation={'dark'} size={'large'} type={'overlay'} />}
              {shouldRenderCalendar && (
                <PostingPlanCalendar
                  timeFormat={settings.selectedOrgSettings?.timeFormat || TimeFormatSetting.HOUR_24}
                  setLoading={props.setLoadingCalendar}
                />
              )}
            </div>
            {props.openBulkDeleteTimeslotsModal && (
              <BulkDeleteTimeslotsModal
                onClose={() => props.setOpenBulkDeleteTimeslotsModal(false)}
                timeslots={props.filteredTimeslots}
              />
            )}
            {props.openCreateEditTimeslotModal && (
              <>
                {!props.profiles.length ? (
                  <Modal
                    size={'small'}
                    onClose={() => props.setOpenCreateEditTimeslotModal(false)}
                    closeOnClickOutside={false}
                    closeOnEsc={true}
                    title={''}
                    primaryButtonAction={() => props.setOpenCreateEditTimeslotModal(false)}
                    primaryButtonTitle={'Close'}
                    primaryButtonColor={ButtonColorsEnum.alert}
                    showCloseIcon={false}>
                    <BoxNotification type={'error'}>
                      There are no Profiles in this Workspace.
                      <br />
                      <Link to={'/settings/profiles'} target={'_blank'}>
                        Connect a Profile
                      </Link>{' '}
                      to get started.
                    </BoxNotification>
                  </Modal>
                ) : (
                  <AddEditTimeslotModal
                    onClose={() => props.setOpenCreateEditTimeslotModal(false)}
                    timeslot={props.selectedTimeslot}
                    hourDayOfWeekTuple={props.dateClicked}
                    filteredCategoryId={props.filters.categories}
                    filteredProfileId={props.filters.profiles}
                    startOfWeek={dayStartOfWeek}
                  />
                )}
                ;
              </>
            )}
            {props.openDeleteTimeslotWarningModal && (
              <DeleteTimeslotModal
                onClose={() => props.setOpenDeleteTimeslotWarningModal(false)}
                onSubmit={() => {
                  props.setOpenDeleteTimeslotWarningModal(false);
                  if (props.selectedTimeslot) {
                    props.deleteTimeslotHandler(props.selectedTimeslot, true);
                  }
                }}
              />
            )}
            {props.openFilterModal && breakpoint === BreakpointsEnum.small && (
              <Modal title={'Filters'} onClose={() => props.setOpenFilterModal(false)} showCloseIcon={true}>
                <TimeslotFilterMenu
                  isMobile={true}
                  filters={props.filters}
                  profiles={props.profiles}
                  categories={props.categories}
                  dispatchFilters={props.dispatchFilters}
                  formInstance={props.timeslotFilterMenuProps.formInstance}
                  timeslots={props.timeslotFilterMenuProps.timeslots}
                  profilesFilteredTimeslots={props.timeslotFilterMenuProps.profilesFilteredTimeslots}
                  categoriesFilteredTimeslots={props.timeslotFilterMenuProps.categoriesFilteredTimeslots}
                  loading={props.loadingCalendar}
                />
              </Modal>
            )}
          </div>
        )}
        {props.openDeleteTimeslotWarningModal && (
          <DeleteTimeslotModal
            onClose={() => props.setOpenDeleteTimeslotWarningModal(false)}
            onSubmit={() => {
              props.setOpenDeleteTimeslotWarningModal(false);
              if (props.selectedTimeslot) {
                props.deleteTimeslotHandler(props.selectedTimeslot, true);
              }
            }}
          />
        )}
        {props.openFilterModal && breakpoint === BreakpointsEnum.small && (
          <Modal title={'Filters'} onClose={() => props.setOpenFilterModal(false)} showCloseIcon={true} isFilter>
            <TimeslotFilterMenu
              isMobile={true}
              filters={props.filters}
              profiles={props.profiles}
              categories={props.categories}
              dispatchFilters={props.dispatchFilters}
              formInstance={props.timeslotFilterMenuProps.formInstance}
              timeslots={props.timeslotFilterMenuProps.timeslots}
              profilesFilteredTimeslots={props.timeslotFilterMenuProps.profilesFilteredTimeslots}
              categoriesFilteredTimeslots={props.timeslotFilterMenuProps.categoriesFilteredTimeslots}
              loading={props.loadingCalendar}
            />
          </Modal>
        )}
      </PostingPlanProvider>
    </FluidContentLayoutView>
  );
};
